
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import clsx from 'clsx'
import type {MDXRemoteSerializeResult} from 'next-mdx-remote'
import {serialize} from 'next-mdx-remote/serialize'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import {createRef, useEffect} from 'react'
import Button from '../components/atoms/button'
import Container from '../components/atoms/container'
import GridContainer from '../components/atoms/grid-container'
import GridItem from '../components/atoms/grid-item'
import Heading from '../components/atoms/heading'
import HighlightedSection from '../components/atoms/highlighted-section'
import Link from '../components/atoms/link'
import LoadableImage from '../components/atoms/loadable-image'
import Paragraph from '../components/atoms/paragraph'
import InfoBox from '../components/molecules/info-box'
import PageCover from '../components/organisms/page-cover'
import OpenPositionSection from '../components/templates/open-position-section'
import {FooterDataStateProps, FooterProvider} from '../contexts/footer-context'
import PageLayout from '../layouts/page-layout'
import defaultImageLoader from '../lib/default-image-loader'
import type {CareersDataProps} from '../lib/fetch-careers-data'
import fetchCareersData from '../lib/fetch-careers-data'
import fetchGeneralSettings from '../lib/fetch-general-settings'
import fetchSocialLinks from '../lib/fetch-social-links'
import mapRawDataWithImage from '../lib/map-raw-data-with-image'
import parseFrontMatter from '../lib/parse-front-matter'
import bishopSymbol from '../public/assets/bishop-logo-symbol.png'
import lovableWorkplace2018 from '../public/assets/lovable-workplace-2018.png'
import lovableWorkplace2019 from '../public/assets/lovable-workplace-2019.png'
import lovableWorkplace2020 from '../public/assets/lovable-workplace-2020.png'
import lovableWorkplace2023 from '../public/assets/lovable-workplace-2023.png'

export default function Careers({
  coverPhoto,
  coverPhotoAlt,
  coverTitle,
  coverDescription,
  metaDescription,
  previewImage,
  benefits,
  activeJobOpenings,
  interviewSteps,
  social,
  general,
}: CareersDataProps<MDXRemoteSerializeResult> & FooterDataStateProps) {
  const {t} = useTranslation('careers')
  const openPositionsAnchor = createRef<HTMLDivElement>()

  function scrollToJobOpenings() {
    if (openPositionsAnchor.current) {
      openPositionsAnchor.current.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      })
    }
  }

  useEffect(() => {
    async function initSmoothscrollPolyfill() {
      const SmoothscrollPolyfill = await import('smoothscroll-polyfill')

      SmoothscrollPolyfill.polyfill()
    }

    initSmoothscrollPolyfill()
  }, [])

  return (
    <FooterProvider value={{social, general}}>
      <PageLayout
        className="grid grid-flow-row gap-16 md:gap-24 pt-24 lg:pt-10 pb-12 md:pb-24"
        disableDefaultPadding
        title={t('common:navigation.careers')}
        description={metaDescription}
        openGraph={{
          images: [{url: previewImage || bishopSymbol.src}],
          url: "careers",
        }}
        prefixImageUrl={!previewImage}
      >
        <div>
          <PageCover
            className="sm:min-h-180"
            title={
              <Heading
                variant="h1"
                disableMaxWidth
                className="text-center lg:text-left lg:max-w-sm font-black"
              >
                {coverTitle}
              </Heading>
            }
            buttons={
              activeJobOpenings.length > 0 && (
                <Button
                  variant="primary"
                  className="justify-self-start"
                  onClick={scrollToJobOpenings}
                >
                  {t('general.open-positions')}
                </Button>
              )
            }
            description={coverDescription}
            media={
              <LoadableImage
                src={coverPhoto}
                alt={coverPhotoAlt}
                layout="responsive"
                width={558}
                height={460}
                objectFit="cover"
                borderRadius="rounded-none sm:rounded-3xl"
                wrapperClassName="max-w-none sm:max-w-xl lg:max-w-none -mx-6 -mt-4 sm:mx-auto sm:mt-0"
                sizes="(max-width: 576px) 576px, (max-width: 640px) 828px, 640px"
                priority
              />
            }
          />

          <HighlightedSection>
            <Heading variant="h2" className="text-center" disableMaxWidth>
              {t('interview-process.title')}
            </Heading>

            {interviewSteps && interviewSteps.length > 0 && (
              <div className="grid gap-8">
                {interviewSteps.map((interviewStep, index) => (
                  <GridContainer
                    disableDefaultColumns
                    className="items-center grid-flow-row md:grid-flow-col grid-cols-1 md:grid-cols-2 gap-4 md:gap-0"
                    key={interviewStep.title}
                  >
                    <GridItem
                      className={clsx(
                        index % 2 === 0
                          ? 'order-1 md:order-1'
                          : 'order-1 md:order-2',
                      )}
                    >
                      <LoadableImage
                        src={interviewStep.image}
                        alt={interviewStep.alt}
                        layout="responsive"
                        width={558}
                        height={460}
                        objectFit="cover"
                        borderRadius="rounded-2xl md:rounded-3xl"
                        sizes="(max-width: 576px) 576px, (max-width: 640px) 828px, 640px"
                      />
                    </GridItem>

                    <GridItem
                      className={clsx(
                        'grid gap-2',
                        index % 2 === 0
                          ? 'order-2 md:order-2'
                          : 'order-2 md:order-1',
                      )}
                    >
                      <Heading variant="h3" className="max-w-none md:max-w-xs">
                        {interviewStep.title}
                      </Heading>

                      <Paragraph className="text-typography text-lg leading-normal">
                        {interviewStep.body}
                      </Paragraph>
                    </GridItem>
                  </GridContainer>
                ))}
              </div>
            )}
          </HighlightedSection>
        </div>

        <Container
          component="section"
          className="grid justify-items-center gap-4 md:gap-8 relative"
        >
          <Heading variant="h2" className="text-center">
            {t('benefits.title')}
          </Heading>

          <GridContainer className="md:grid-cols-3 gap-y-8 gap-x-12 w-full">
            {benefits.map(({title, body}) => (
              <InfoBox title={title} key={title}>
                {body}
              </InfoBox>
            ))}
          </GridContainer>
        </Container>

        <Container
          component="section"
          className="grid justify-items-center gap-4 md:gap-8 relative"
        >
          <Heading variant="h2" className="text-center">
            {t('awards.title')}
          </Heading>

          <GridContainer
            className="grid-cols-2 lg:grid-cols-4 gap-6 md:gap-24 w-full justify-items-center md:px-16"
            disableDefaultColumns
          >
            <Link
              href="https://dreamjo.bs/en/company/bishop-co-kft?ref=bishop-website"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <Image
                src={lovableWorkplace2018}
                alt="Lovable Workplace award - 2018"
                layout="responsive"
                loader={defaultImageLoader}
              />
            </Link>

            <Link
              href="https://dreamjo.bs/en/company/bishop-co-kft?ref=bishop-website"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <Image
                src={lovableWorkplace2019}
                alt="Lovable Workplace award - 2019"
                layout="responsive"
                loader={defaultImageLoader}
              />
            </Link>

            <Link
              href="https://dreamjo.bs/en/company/bishop-co-kft?ref=bishop-website"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <Image
                src={lovableWorkplace2020}
                alt="Lovable Workplace award - 2020"
                layout="responsive"
                loader={defaultImageLoader}
              />
            </Link>

            <Link
              href="https://dreamjo.bs/en/company/bishop-co-kft?ref=bishop-website"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <Image
                src={lovableWorkplace2023}
                alt="Lovable Workplace award - 2023"
                layout="responsive"
                loader={defaultImageLoader}
              />
            </Link>
          </GridContainer>

          <div
            ref={openPositionsAnchor}
            className="w-1 h-1 bg-transparent absolute bottom-12 md:bottom-6"
          />
        </Container>

        {activeJobOpenings && activeJobOpenings.length > 0 && (
          <OpenPositionSection
            title={t('job-openings.title')}
            openPositions={activeJobOpenings}
          />
        )}
      </PageLayout>
    </FooterProvider>
  )
}

 async function _getStaticProps() {
  const {
    coverPhoto,
    coverPhotoAlt,
    coverTitle,
    coverDescription: rawCoverDescription,
    metaDescription,
    previewImage,
    benefits,
    activeJobOpenings,
    interviewSteps,
  } = fetchCareersData()

  const careerContents = await Promise.all(
    activeJobOpenings.map(name => import(`../content/job-openings/${name}.md`)),
  )

  const coverDescription = await serialize(rawCoverDescription)

  return {
    props: {
      coverPhoto,
      coverPhotoAlt,
      coverTitle,
      coverDescription,
      metaDescription,
      previewImage,
      benefits,
      activeJobOpenings: careerContents
        .map(content => parseFrontMatter<any>(content.default).attributes)
        .map(data => ({
          title: data.title,
          shortDescription: data['short-description'],
          categories: data.categories,
          description: data.description,
        })),
      interviewSteps: interviewSteps.map(mapRawDataWithImage),
      social: fetchSocialLinks(),
      general: fetchGeneralSettings(),
    },
  }
}


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/careers',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  